import { ComponentIds, ReplaceUtils } from '@inter-ikea-core/core';
import { RotateOrientation } from '../types';
import { ProductType } from '@inter-ikea-storageone/range';
/**
 * Finds all inserts and flip the x and y values for the connection key.
 */
export const getInsertPositions = (jonaxelEntityApi, childEntities) => childEntities.reduce((acc, childEntity) => {
    const childRef = jonaxelEntityApi.getProductRef(childEntity);
    if (!childRef)
        return acc;
    const guestConnectionsComponent = childEntity.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID);
    guestConnectionsComponent?.connections.forEach(connection => {
        const [start, part1, part2] = connection.hostConnector.key.split('-');
        const isCompartmentLarge = connection.hostConnector.key.includes('large-compartment');
        const reversedConnectionKey = [start, part2, part1].join('-');
        acc.push({
            childRef,
            connection: isCompartmentLarge ? connection.hostConnector.key : reversedConnectionKey
        });
    });
    return acc;
}, []);
/**
 * Replaces the current selected entity with a rotated version
 */
export const replaceJonaxelEntity = (entityApi, selectedEntity, productRef) => {
    const orientation = entityApi.getProductType(selectedEntity) === ProductType.KALLAX_FRAME
        ? productRef.includes(RotateOrientation.Horizontal)
            ? RotateOrientation.Vertical
            : RotateOrientation.Horizontal
        : productRef.includes(RotateOrientation.Default)
            ? RotateOrientation.Rotated
            : RotateOrientation.Default;
    const newProduct = `${productRef.replace(/\D/g, '')}--${orientation}`;
    const newEntity = entityApi.create(newProduct);
    if (!newEntity)
        return;
    return ReplaceUtils.replaceEntity(selectedEntity, newEntity, 0, entityApi.core.services)
        .replacedEntityResult;
};
/**
 * Find any inserts in entity
 */
export const findInserts = (entity) => {
    const entityArray = entity.getComponent(ComponentIds.PARENT_COMPONENT_ID)
        ?.children;
    return entityArray;
};
/**
 * Adds inserts after changing orientation on the frame.
 */
export const addInserts = (jonaxelEntityApi, childEntitiesPosition, replacedEntity, productRef) => {
    const frameOrientation = productRef.includes(RotateOrientation.Vertical)
        ? RotateOrientation.Vertical
        : RotateOrientation.Horizontal;
    const reversedIndexMap = createReversedIndexMap(replacedEntity, frameOrientation);
    if (!reversedIndexMap)
        return;
    const placementResult = childEntitiesPosition.map(child => {
        const newEntity = jonaxelEntityApi.create(child.childRef);
        if (!newEntity) {
            return { valid: false };
        }
        const isCompartmentLarge = child.connection.includes('large-compartment');
        const insertType = isCompartmentLarge ? 'insert-large-compartment' : 'insert-box';
        if (isCompartmentLarge) {
            // If it's a large compartment, skip swapInsertPosition and other logic
            return jonaxelEntityApi.placementService.snapToConnector(newEntity, insertType, replacedEntity, child.connection);
        }
        // Otherwise, proceed with swapInsertPosition and other logic
        const connectionKey = swapInsertPosition(frameOrientation, child.connection, reversedIndexMap);
        return jonaxelEntityApi.placementService.snapToConnector(newEntity, insertType, replacedEntity, connectionKey);
    });
    if (!placementResult.every(result => result.valid)) {
        throw new Error('Not possible to place all inserts when rotating frame');
    }
};
/**
 * Find how many compartments there is in a frame based on the orientation
 */
export const getFrameCompartments = (entity, orientation) => {
    const connections = entity.getComponent(ComponentIds.CONNECTORS_COMPONENT_ID)
        ?.connectors;
    if (!connections)
        return;
    if (orientation === RotateOrientation.Horizontal) {
        const connector = connections.find(connector => connector.key.includes('column'));
        return connector.subConnectorCount;
    }
    else {
        return connections.filter(connector => connector.key.includes('column')).length;
    }
};
/**
* Creates a map to see which value should be switched with what
* frame with 4 compartments
  0 -> 3
  1 -> 2
  2 -> 1
  3 -> 0
* frame with 3 compartments
  0 -> 2
  1 -> 1
  2 -> 0
*/
export const createReversedIndexMap = (entity, orientation) => {
    const frameCompartments = getFrameCompartments(entity, orientation);
    if (!frameCompartments)
        return;
    return Array.from({ length: frameCompartments }).reduce((map, _, i) => {
        map[i.toString()] = (frameCompartments - 1 - i).toString();
        return map;
    }, {});
};
/**
 * When rotating the frame, the inserts needs to be moved depending on which orientation the frame is changed to.
 * This will return a new connection key
 */
export const swapInsertPosition = (orientation, connectionKey, reversedIndexMap) => {
    const [start, part1, part2] = connectionKey.split('-');
    return orientation === RotateOrientation.Vertical
        ? [start, reversedIndexMap[part1], part2].join('-')
        : [start, part1, reversedIndexMap[part2]].join('-');
};
//# sourceMappingURL=rotateUtils.js.map